import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { PasswordField } from "@/components/ui/PasswordField";
import { ApiError } from "@/lib/api";
import { useChangeOwnPassword } from "@/queries/auth";

export const Route = createFileRoute("/force-change-password")({
  component: ForceChangePasswordPage,
});

function ForceChangePasswordPage() {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const changePassword = useChangeOwnPassword();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorKey(null);

    if (newPassword.length < 8) {
      setErrorKey("tooShort");
      return;
    }
    if (newPassword !== confirm) {
      setErrorKey("mismatch");
      return;
    }

    changePassword.mutate(
      { current_password: currentPassword, new_password: newPassword },
      {
        onSuccess: () => {
          setDone(true);
          setTimeout(() => {
            void navigate({ to: "/world" });
          }, 600);
        },
        onError: (err) => {
          if (err instanceof ApiError && err.status === 400) {
            setErrorKey("currentWrong");
          } else {
            setErrorKey("generic");
          }
        },
      },
    );
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle>{t("forceChange.title")}</CardTitle>
        <CardDescription>{t("forceChange.subtitle")}</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
          <div className="flex flex-col gap-2">
            <Label htmlFor="fcp-current">{t("forceChange.currentLabel")}</Label>
            <PasswordField
              id="fcp-current"
              name="current_password"
              autoComplete="current-password"
              required
              placeholder={t("forceChange.currentPlaceholder")}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              disabled={changePassword.isPending || done}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="fcp-new">{t("forceChange.newLabel")}</Label>
            <PasswordField
              id="fcp-new"
              name="new_password"
              autoComplete="new-password"
              required
              minLength={8}
              placeholder={t("forceChange.newPlaceholder")}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={changePassword.isPending || done}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="fcp-confirm">{t("forceChange.confirmLabel")}</Label>
            <PasswordField
              id="fcp-confirm"
              name="confirm_password"
              autoComplete="new-password"
              required
              minLength={8}
              placeholder={t("forceChange.confirmPlaceholder")}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              disabled={changePassword.isPending || done}
            />
          </div>
          {errorKey && (
            <Alert variant="destructive">
              <AlertDescription>{t(`forceChange.errors.${errorKey}`)}</AlertDescription>
            </Alert>
          )}
          {done && (
            <Alert>
              <AlertDescription>{t("forceChange.success")}</AlertDescription>
            </Alert>
          )}
          <Button
            type="submit"
            disabled={
              changePassword.isPending ||
              done ||
              !currentPassword ||
              !newPassword ||
              !confirm
            }
          >
            {changePassword.isPending ? t("forceChange.submitting") : t("forceChange.submit")}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
